import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import useSWR from 'swr';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { GlobalContext } from '../../contexts/GlobalContext';
import Badge from '../base/Badge';
import Button, { ClickableArea } from '../base/Button';
import { StyledCardSection } from '../base/Card';
import Icon from '../base/Icon';
import Loading from '../base/Loading';
import Text from '../base/Text';
import { Div } from '../helpers/StyledUtils';
import BulkOperationBreakdown from './BulkOperationBreakdown';
import BulkOperationProgress from './BulkOperationProgress';
import { BulkOperationModel, BulkOperationType } from './types';
import { BULK_OPERATION_CONFIGS, getApiMethod } from './utils';
import { prettyPrintDate } from './utils';
import { useDatePreferences } from '../../hooks/useDatePreferences';
interface BulkOperationHistoryProps {
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  filters: object;
  revalidateCounts: () => void;
}
const BulkOperationHistory: React.FC<BulkOperationHistoryProps> = t0 => {
  const $ = _c(55);
  const {
    operation_type,
    model,
    filters,
    revalidateCounts
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [open_id, setOpenedId] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [cursor, setCursor] = useState(t1);
  const config = BULK_OPERATION_CONFIGS[operation_type];
  let t2;
  if ($[1] !== model || $[2] !== operation_type) {
    t2 = getApiMethod(operation_type, model);
    $[1] = model;
    $[2] = operation_type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const api_method = t2;
  let t3;
  if ($[4] !== api_method || $[5] !== config || $[6] !== operation_type) {
    t3 = () => {
      if (operation_type === "cancel") {
        return APIMethodKeys.bulk.cancelEvents;
      }
      return APIMethodKeys[config.list_api_key][api_method];
    };
    $[4] = api_method;
    $[5] = config;
    $[6] = operation_type;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const getApiMethodKey = t3;
  let t4;
  if ($[8] !== cursor || $[9] !== filters || $[10] !== getApiMethodKey) {
    t4 = getApiMethodKey().list({
      ...filters,
      ...cursor,
      limit: 5,
      dir: "desc"
    });
    $[8] = cursor;
    $[9] = filters;
    $[10] = getApiMethodKey;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== HookdeckAPI || $[13] !== api_method || $[14] !== config || $[15] !== cursor || $[16] !== filters || $[17] !== operation_type) {
    t5 = () => {
      let api_call;
      if (operation_type === "cancel") {
        api_call = HookdeckAPI.bulk.cancelEvents.list({
          ...filters,
          ...cursor,
          limit: 5,
          dir: "desc"
        });
      } else {
        api_call = HookdeckAPI[config.list_api_key][api_method].list({
          ...filters,
          ...cursor,
          limit: 5,
          dir: "desc"
        });
      }
      if (operation_type === "cancel") {
        return api_call.then(_temp2);
      }
      return api_call;
    };
    $[12] = HookdeckAPI;
    $[13] = api_method;
    $[14] = config;
    $[15] = cursor;
    $[16] = filters;
    $[17] = operation_type;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      refreshInterval: 5000
    };
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const {
    data: bulk_operations,
    mutate: mutateBulkOperations
  } = useSWR(t4, t5, t6);
  let t7;
  if ($[20] !== bulk_operations?.models || $[21] !== open_id || $[22] !== revalidateCounts) {
    t7 = () => {
      revalidateCounts();
      !open_id && setOpenedId(bulk_operations?.models[0]?.id ?? "");
    };
    $[20] = bulk_operations?.models;
    $[21] = open_id;
    $[22] = revalidateCounts;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== bulk_operations || $[25] !== revalidateCounts) {
    t8 = [bulk_operations, revalidateCounts];
    $[24] = bulk_operations;
    $[25] = revalidateCounts;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  useEffect(t7, t8);
  let t9;
  if ($[27] !== open_id) {
    t9 = id => () => setOpenedId(open_id === id ? "" : id);
    $[27] = open_id;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const curried_toggle_opened = t9;
  if (!bulk_operations) {
    let t10;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <StyledCardSection p={5}><Loading /></StyledCardSection>;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    return t10;
  }
  if (bulk_operations.models.length === 0) {
    const t10 = operation_type === "cancel" ? "cancellations" : `${model ? model : "operations"}`;
    let t11;
    if ($[30] !== t10) {
      t11 = <StyledCardSection p={4}><Text muted={true}>No bulk{" "}{t10}.</Text></StyledCardSection>;
      $[30] = t10;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    return t11;
  }
  let t10;
  if ($[32] !== bulk_operations.models || $[33] !== curried_toggle_opened || $[34] !== model || $[35] !== mutateBulkOperations || $[36] !== open_id || $[37] !== operation_type || $[38] !== revalidateCounts || $[39] !== timezone) {
    let t11;
    if ($[41] !== curried_toggle_opened || $[42] !== model || $[43] !== mutateBulkOperations || $[44] !== open_id || $[45] !== operation_type || $[46] !== revalidateCounts || $[47] !== timezone) {
      t11 = bulk_operation => <StyledCardSection key={bulk_operation.id} muted={open_id === bulk_operation.id}><ClickableArea onClick={curried_toggle_opened(bulk_operation.id)} w={100} flex={{
          justify: "space-between",
          align: "center"
        }} p={4}><Div flex={{
            align: "center"
          }}><Icon icon={open_id === bulk_operation.id ? "expand_less" : "expand_more"} muted={true} left={3} /><Text semi={true} dark={true}>#{bulk_operation.number}</Text><Text semi={true} dark={true} m={{
              x: 6
            }}>{prettyPrintDate(bulk_operation.created_at, timezone)}</Text><Text muted={true} mono={true}>{bulk_operation.id}</Text></Div>{bulk_operation.cancelled_at ? <Badge subtle={true} neutral={true}>Aborted</Badge> : bulk_operation.completed_at ? <Badge subtle={true} success={true}><NumberFormat renderText={_temp3} displayType="text" value={bulk_operation.completed_count || 0} thousandSeparator="," /></Badge> : bulk_operation.in_progress ? <Badge subtle={true} warning={true}><NumberFormat renderText={_temp4} displayType="text" value={bulk_operation.completed_count || 0} thousandSeparator="," />/<NumberFormat renderText={_temp5} displayType="text" value={bulk_operation.estimated_count || 0} thousandSeparator="," /></Badge> : <Badge subtle={true} danger={true}>Failed</Badge>}</ClickableArea>{open_id === bulk_operation.id && <Div p={{
          x: 4,
          b: 4
        }} m={{
          t: 0.25
        }}><BulkOperationBreakdown operation_type={operation_type} model={model} query={bulk_operation.query} /><BulkOperationProgress operation={bulk_operation} operation_type={operation_type} model={model} onCancel={() => {
            mutateBulkOperations();
            revalidateCounts();
          }} /></Div>}</StyledCardSection>;
      $[41] = curried_toggle_opened;
      $[42] = model;
      $[43] = mutateBulkOperations;
      $[44] = open_id;
      $[45] = operation_type;
      $[46] = revalidateCounts;
      $[47] = timezone;
      $[48] = t11;
    } else {
      t11 = $[48];
    }
    t10 = bulk_operations.models.map(t11);
    $[32] = bulk_operations.models;
    $[33] = curried_toggle_opened;
    $[34] = model;
    $[35] = mutateBulkOperations;
    $[36] = open_id;
    $[37] = operation_type;
    $[38] = revalidateCounts;
    $[39] = timezone;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[49] !== bulk_operations.pagination?.next || $[50] !== bulk_operations.pagination?.prev) {
    t11 = (bulk_operations.pagination?.next || bulk_operations.pagination?.prev) && <StyledCardSection flex={{
      justify: "flex-end",
      gap: 2
    }} p={{
      x: 4,
      y: 3
    }}><Button disabled={!bulk_operations.pagination?.prev} onClick={() => setCursor({
        prev: bulk_operations.pagination?.prev,
        next: undefined
      })} outline={true} icon="chevron_left" /><Button disabled={!bulk_operations.pagination?.next} onClick={() => setCursor({
        next: bulk_operations.pagination?.next,
        prev: undefined
      })} outline={true} icon="chevron_right" /></StyledCardSection>;
    $[49] = bulk_operations.pagination?.next;
    $[50] = bulk_operations.pagination?.prev;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== t10 || $[53] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[52] = t10;
    $[53] = t11;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  return t12;
};
export default BulkOperationHistory;
function _temp(op) {
  return op.in_progress;
}
function _temp2(list) {
  return {
    models: list.models,
    pagination: list.pagination,
    count: list.models.filter(_temp).length
  };
}
function _temp3(v) {
  return v;
}
function _temp4(v_0) {
  return v_0;
}
function _temp5(v_1) {
  return v_1;
}